import type { ChangeEvent, CSSProperties, FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { ProjectSummary } from '@/types'

import Link from 'next/link'
import { useCallback, useMemo, useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getProjects } from '@/lib/content'
import { shouldRethrow } from '@/lib/db'
import { countWord, pad2, shapeKey } from '@/lib/format'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { FigLabel } from '@/components/generals/fig-label'
import { PointCloud } from '@/components/generals/point-cloud'
import { Reveal } from '@/components/generals/reveal'
import { AtlasMap } from '@/components/atlas/atlas-map'

import { OFFICES } from '@/constants/navigation'

interface AtlasPageProps {
    projects: Array<ProjectSummary>
}

const TYPES = ['All', 'Civic', 'Cultural', 'Housing', 'Infrastructure']
const YEAR_MIN = 2021
const YEAR_MAX = 2026
const FILTER_WIDTH = 320

const delay = (value: number): CSSProperties => ({ animationDelay: `${value}ms` })

const AtlasPage: FC<AtlasPageProps> = ({ projects = [] }): ReactNode => {
    const [type, setType] = useState('All')
    const [year, setYear] = useState(YEAR_MAX)
    const numbers = useMemo(() => new Map(projects.map((project, index) => [project.id, pad2(index + 1)])), [projects])

    const isVisible = useCallback((project: ProjectSummary): boolean => {
        const typeMatch = type === 'All' || project.buildingType.trim().toLowerCase() === type.toLowerCase()

        return typeMatch && project.year <= year
    }, [type, year])

    const shown = projects.filter(project => project.lat !== null && project.lon !== null && isVisible(project))
    const fill = ((year - YEAR_MIN) / (YEAR_MAX - YEAR_MIN)) * 100

    const filters = (
        <div className='flex h-full flex-col gap-8 overflow-y-auto px-gutter py-8 desk:px-6 desk:pl-gutter'>
            <div>
                <div data-ruler-mark='01' className='rise-fade' style={delay(0)}>
                    <p className='hud text-ink'>
                        Fig. 01 / Atlas
                    </p>

                    <p className='hud text-mute'>
                        Europe and USA
                    </p>

                    <p className='hud text-mute'>
                        {OFFICES.length} offices / {projects.length} buildings
                    </p>
                </div>

                <h1 className='display-lg mt-8 desk:text-[40px]'>
                    <span className='rise-line'>
                        <span className='rise-inner text-ink' style={delay(80)}>
                            {countWord(OFFICES.length)} offices,
                        </span>
                    </span>

                    <span className='rise-line'>
                        <span className='rise-inner text-dim' style={delay(170)}>
                            {countWord(projects.length).toLowerCase()} buildings.
                        </span>
                    </span>
                </h1>
            </div>

            <div role='group' aria-labelledby='atlas-type-label'>
                <p id='atlas-type-label' className='hud mb-3 text-mute'>
                    Type
                </p>

                <div className='flex flex-wrap gap-2'>
                    {TYPES.map(item => (
                        <button
                            key={item}
                            type='button'
                            aria-pressed={type === item}
                            onClick={() => setType(item)}
                            className={cn(
                                'hud h-9 border px-3 transition-colors duration-300',
                                type === item ? 'border-ink bg-ink text-background' : 'border-hairline text-mute hover:border-mute hover:text-ink'
                            )}
                        >
                            {item}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <div className='flex items-baseline justify-between gap-4'>
                    <label htmlFor='atlas-year' className='hud text-mute'>
                        Completed by
                    </label>

                    <output htmlFor='atlas-year' className='font-heading text-[28px] font-medium leading-none tracking-[-0.03em] text-ink'>
                        {year}
                    </output>
                </div>

                <input
                    id='atlas-year'
                    type='range'
                    min={YEAR_MIN}
                    max={YEAR_MAX}
                    step={1}
                    value={year}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setYear(Number(event.target.value))}
                    className='hud-range mt-4 w-full'
                    style={{ '--fill': `${fill}%` } as CSSProperties}
                />

                <p className='hud mt-2 flex justify-between text-mute' aria-hidden='true'>
                    {Array.from({ length: YEAR_MAX - YEAR_MIN + 1 }, (_, index) => (
                        <span key={index}>
                            {String(YEAR_MIN + index).slice(2)}
                        </span>
                    ))}
                </p>
            </div>

            <div>
                <p className='hud text-mute' aria-live='polite'>
                    Showing {pad2(shown.length)} / {pad2(projects.length)}
                </p>

                <ul className='mt-3 border-t border-hairline'>
                    {projects.map(project => {
                        const visible = isVisible(project)

                        return (
                            <li key={project.id} className={cn('flex items-baseline justify-between gap-4 border-b border-hairline py-2.5 transition-opacity duration-500', visible ? 'opacity-100' : 'opacity-35')}>
                                <span className='hud flex min-w-0 gap-3'>
                                    <span className='text-mute'>
                                        {numbers.get(project.id)}
                                    </span>

                                    <span className='truncate text-ink' data-mdlfy-cms={`projects:${project.id}:name`}>
                                        {project.name}
                                    </span>
                                </span>

                                <span className='hud shrink-0 text-mute'>
                                    {project.year}
                                </span>
                            </li>
                        )
                    })}
                </ul>
            </div>

            <div className='mt-auto flex flex-wrap items-center gap-x-5 gap-y-2' aria-hidden='true'>
                <span className='hud flex items-center gap-2 text-mute'>
                    <span className='size-2.5 border border-signal' />
                    Office
                </span>

                <span className='hud flex items-center gap-2 text-mute'>
                    <span className='size-2 bg-signal' />
                    Project
                </span>

                <span className='hud flex items-center gap-2 text-mute'>
                    <span className='w-5 border-t border-dashed border-signal' />
                    Lead office
                </span>
            </div>
        </div>
    )

    return (
        <Layout>
            <Seo
                title='Atlas'
                description='A dotted map of Europe and the USA with Norrvik’s offices in Stockholm, Lisbon and New York and every monitored building, filterable by type and year.'
                figure='Fig. 01 / Atlas / Europe and USA'
                shape='globe'
            />

            <section aria-label='Atlas of offices and projects' className='relative border-b border-hairline'>
                <div className='border-b border-hairline bg-background desk:absolute desk:inset-y-0 desk:left-0 desk:z-[6] desk:border-b-0 desk:border-r desk:bg-panel/95' style={{ '--filter-width': `${FILTER_WIDTH}px` } as CSSProperties}>
                    <div className='desk:h-full desk:w-[var(--filter-width)]'>
                        {filters}
                    </div>
                </div>

                <AtlasMap
                    projects={projects}
                    idPrefix='atlas-page'
                    isVisible={isVisible}
                    leftInset={FILTER_WIDTH}
                    stageClassName='aspect-square sm:aspect-[4/3] desk:aspect-auto desk:h-[calc(100svh-4rem)] desk:min-h-[640px]'
                    panelExtra={project => (
                        <div className='relative mb-6 aspect-[4/3] w-full border border-hairline'>
                            <PointCloud
                                key={project.id}
                                shape={shapeKey(project.shape, project.slug)}
                                label={`Small rotating point cloud of ${project.name}`}
                                budget='small'
                                rotate='always'
                                rotateSpeed={10}
                                effects={false}
                                frame
                                crosshair={false}
                                className='absolute inset-0'
                            />
                        </div>
                    )}
                />
            </section>

            <section aria-labelledby='atlas-index-title'>
                <Reveal className='px-gutter pb-10 pt-20 desk:pt-28'>
                    <FigLabel fig='02' title='Index' detail='By lead office' note='Stockholm leads the north, Lisbon the Atlantic coast, New York the USA' />

                    <h2 id='atlas-index-title' className='sr-only'>
                        Projects by lead office
                    </h2>
                </Reveal>

                <div className='grid gap-px border-t border-hairline bg-hairline desk:grid-cols-3'>
                    {OFFICES.map(office => {
                        const led = projects.filter(project => project.leadOffice === office.code)

                        return (
                            <div key={office.code} className='bg-background px-gutter py-10'>
                                <p className='hud text-ink'>
                                    {office.city}
                                    <span className='text-mute'>
                                        {' / '}
                                        {office.code} / {pad2(led.length)} projects
                                    </span>
                                </p>

                                <ul className='mt-6 border-t border-hairline'>
                                    {led.map(project => (
                                        <li key={project.id} className='group border-b border-hairline'>
                                            <Link href={`/projects/${project.slug}`} className='grid grid-cols-[2.5rem_minmax(0,1fr)_auto] items-baseline gap-4 py-4'>
                                                <span className='hud text-mute'>
                                                    {numbers.get(project.id)}
                                                </span>

                                                <span className='min-w-0'>
                                                    <span className='block truncate font-heading text-[22px] font-medium tracking-[-0.02em] text-ink transition-transform duration-500 ease-instrument group-hover:translate-x-1' data-mdlfy-cms={`projects:${project.id}:name`}>
                                                        {project.name}
                                                    </span>

                                                    <span className='hud mt-1 flex gap-3 text-mute'>
                                                        <span data-mdlfy-cms={`projects:${project.id}:city`}>
                                                            {project.city}
                                                        </span>

                                                        <span data-mdlfy-cms={`projects:${project.id}:building_type`}>
                                                            {project.buildingType}
                                                        </span>

                                                        <span>
                                                            {project.year}
                                                        </span>
                                                    </span>
                                                </span>

                                                <ArrowUpRight className='size-4 text-ink' strokeWidth={1.5} aria-hidden='true' />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )
                    })}
                </div>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<AtlasPageProps> = async () => {
    try {
        const projects = await getProjects()

        return { props: { projects }, revalidate: 60 }
    }
    catch (error) {
        console.error('Atlas projects could not be loaded:', error instanceof Error ? error.message : error)

        if (shouldRethrow()) throw error

        return { props: { projects: [] }, revalidate: 30 }
    }
}

export default AtlasPage
